import { doc, setDoc, collection, serverTimestamp } from 'firebase/firestore';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase';
import { toast } from 'react-toastify';
import { FiUpload, FiFile, FiUser, FiType } from 'react-icons/fi';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';

export default function UploadDocuments() {
  const auth = getAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [formData, setFormData] = useState({
    documentName: '',
    subject: '',
    referralUser: '',
  });
  const { documentName, subject, referralUser } = formData;

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.id]: e.target.value,
    });
  };

  const handleFileChange = (e) => {
    if (e.target.files) {
      setFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!file) {
      toast.error('Please select a document to upload');
      setLoading(false);
      return;
    }

    try {
      // Create storage reference
      const storage = getStorage();
      const fileName = `${auth.currentUser.uid}-${Date.now()}-${file.name}`;
      const storageRef = ref(storage, `documents/${fileName}`);
      
      // Upload file to storage
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        'state_changed',
        null,
        (error) => {
          toast.error('Error uploading file');
          setLoading(false);
        },
        async () => {
          // Get download URL after successful upload
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

          // Create document in Firestore
          const docRef = doc(collection(db, 'documents'));
          await setDoc(docRef, {
            documentName,
            subject,
            referralUser,
            fileUrl: downloadURL,
            fileName: file.name,
            fileType: file.type,
            uploadedBy: auth.currentUser.uid,
            uploadedByName: auth.currentUser.displayName || 'Anonymous',
            uploadedAt: serverTimestamp(),
            status: 'pending',
          });

          toast.success('Document uploaded successfully!');
          setLoading(false);
          navigate('/profile');
        }
      );
    } catch (error) {
      toast.error('Error uploading document');
      setLoading(false);
      console.error(error);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">Upload Documents</h1>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Document Name */}
            <div className="space-y-2">
              <label htmlFor="documentName" className="flex items-center text-sm font-medium text-gray-700">
                <FiType className="mr-2" /> Document Name
              </label>
              <input
                type="text"
                id="documentName"
                value={documentName}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter document name"
              />
            </div>

            {/* Subject */}
            <div className="space-y-2">
              <label htmlFor="subject" className="flex items-center text-sm font-medium text-gray-700">
                <FiFile className="mr-2" /> Subject
              </label>
              <input
                type="text"
                id="subject"
                value={subject}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter subject"
              />
            </div>

            {/* Referral User */}
            <div className="space-y-2">
              <label htmlFor="referralUser" className="flex items-center text-sm font-medium text-gray-700">
                <FiUser className="mr-2" /> Document Referral User
              </label>
              <input
                type="text"
                id="referralUser"
                value={referralUser}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter referral user (optional)"
              />
            </div>

            {/* File Upload */}
            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-gray-700">
                <FiUpload className="mr-2" /> Document File
              </label>
              <input
                type="file"
                id="file"
                onChange={handleFileChange}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* File Info */}
          {file && (
            <div className="border rounded-lg p-4 bg-gray-50">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Selected Document</h3>
              <div className="flex items-center space-x-4">
                <FiFile className="w-12 h-12 text-gray-500" />
                <div>
                  <p className="text-sm font-medium text-gray-900">{file.name}</p>
                  <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(2)} KB</p>
                  <p className="text-xs text-gray-500">{file.type}</p>
                </div>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Uploading...' : 'Upload to System'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}